"use client";

import React, { useRef, useState } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";

export default function FAQSection() {
  const ref = useRef<HTMLDivElement>(null);

  const isInView = useInView(ref, {
    once: true,
    margin: "-50px",
  });

  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: "How long does shipping take?",
      answer: "5-7 business days within the US. Free standard shipping included.",
    },
    {
      question: "How do I charge it?",
      answer: "Plug the included USB cable into any port. 2-hour charge lasts 2-3 weeks.",
    },
    {
      question: "Is it windproof?",
      answer: "Yes, works in heavy wind, rain, and extreme weather conditions.",
    },
    {
      question: "What if it breaks?",
      answer: "30-day money back guarantee plus 1-year manufacturer warranty.",
    },
    {
      question: "How many cigarettes does it hold?",
      answer: "Holds 6-8 regular cigarettes or 4-6 100s, depending on brand.",
    },
    {
      question: "Is it TSA approved for flights?",
      answer: "Check current TSA guidelines. Rechargeable lighters have specific rules.",
    },
    {
      question: "Does it work in cold weather?",
      answer: "Yes, designed to function in temperatures from -10°F to 120°F.",
    },
    {
      question: "Can I buy multiple as gifts?",
      answer: "Absolutely. Each comes in a premium gift box ready for giving.",
    },
    {
      question: "What's the battery life?",
      answer: "300-500 lights per charge with normal use. Lasts 2-3 weeks typically.",
    },
    {
      question: "Is it safe around children?",
      answer: "Features child-resistant design, but keep all lighters away from children.",
    },
    {
      question: "What if I lose the charging cable?",
      answer: "Uses standard USB-C. Any phone charger cable works as replacement.",
    },
    {
      question: "Does it come in different colors?",
      answer: "Currently available in tactical black and gunmetal gray.",
    },
    {
      question: "How do I clean it?",
      answer: "Included microfiber cloth. Wipe exterior, avoid getting water in charging port.",
    },
    {
      question: "Will it work with my cigarette brand?",
      answer: "Yes, designed to fit all major cigarette sizes and brands perfectly.",
    },
  ];

  return (
    <section
      ref={ref}
      className="py-12 sm:py-16 md:py-24 lg:py-32 bg-white"
    >
      <div className="max-w-4xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-6 sm:mb-10 md:mb-16"
        >
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-black mb-2 sm:mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-black/60 text-xs sm:text-sm md:text-base lg:text-lg">
            Everything you need to know.
          </p>
        </motion.div>

        <div className="space-y-2 sm:space-y-3 md:space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className="border border-black/10 rounded-lg overflow-hidden"
            >
              <button
                onClick={() =>
                  setOpenIndex(openIndex === index ? null : index)
                }
                className="w-full px-3 sm:px-4 md:px-6 py-3 sm:py-4 md:py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer active:bg-gray-100"
              >
                <span className="font-serif text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl text-black pr-2 sm:pr-4">
                  {faq.question}
                </span>
                <motion.i
                  animate={{
                    rotate: openIndex === index ? 180 : 0,
                  }}
                  transition={{ duration: 0.3 }}
                  className="ri-arrow-down-s-line text-lg sm:text-xl md:text-2xl text-black/60 flex-shrink-0"
                />
              </button>

              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-3 sm:px-4 md:px-6 pb-3 sm:pb-4 md:pb-5 text-black/70 text-[11px] sm:text-xs md:text-sm lg:text-base leading-relaxed">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
